import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock3, FileAudio2, Headphones } from "lucide-react";
import MockNavigation from "./MockNavigation";
import KeyBoardCheckPage from "./KeyboardCheckPage";
import HeadphoneCheckPage from "./HeadphonePage";
import MicrophoneCheckPage from "./MicrophonePage";
import SummarizeSpokenTestMockPage from "./Listening/SummarizeSpokenTextMock";
import MCQSingleAnswerMockPage from "./Listening/MCQSingleAnswerMock";
import MCQMultipleAnswerMockPage from "./Listening/MCQMultipleAnswerMock";
import FillInTheBlanksMockPage from "./Listening/FillInTheBlanksMock";
import HighlightCorrectSummaryMockPage from "./Listening/HighlightCorrectSummaryMock";
import SelectMissingWordsMockPage from "./Listening/SelectMissingWordMock";
import HighlightIncorrectWordMockPage from "./Listening/HighlightIncorrectWordMock";
import WriteFromDictationMockPage from "./Listening/WriteFromDictationMock";
import Notification from "../QuestionPage/Notification";
import ConfirmDialog from "./PopUp";
import Loading from "./SpinnerLoading";
import Timer from "./Timer";
import ListeningSectionTable from "./ListeningSectionTable";
import TimeoutPopup from "./TimeoutPopup";

export default function ListeningMockTestPage() {
  const navigate = useNavigate();
  const [MockData, setMockData] = useState({ data: {} });
  const [userInputs, setUserInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = 22;
  const totalQuestionPages = 18;
  const testStarted = currentPage >= 5;
  const questionPageNumber = testStarted ? currentPage - 4 : 0;
  const pageProgress = Math.round((currentPage / totalPages) * 100);

  useEffect(() => {
    const fetchMockData = async () => {
      try {
        setLoading(true);
        const response = await fetch("/api/listeningmockquestions/all", {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch listening mock data: ${response.status}`);
        }

        const data = await response.json();
        setMockData({ data });
      } catch (error) {
        console.error("Error fetching listening mock data:", error);
        setMessage("Failed to load listening mock questions.");
        setNotificationType("error");
        setShowNotification(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMockData();
  }, []);

  const attemptedResponses = Object.values(userInputs).reduce((total, answers) => {
    if (!answers || typeof answers !== "object") return total;

    return (
      total +
      Object.values(answers).filter((answer) => {
        if (Array.isArray(answer)) return answer.length > 0;
        if (typeof answer === "string") return answer.trim().length > 0;
        return Boolean(answer);
      }).length
    );
  }, 0);

  const handleInputChange = (questionType, questionIndex, answer) => {
    setUserInputs((prevInputs) => ({
      ...prevInputs,
      [questionType]: {
        ...prevInputs[questionType],
        [questionIndex]: answer,
      },
    }));
  };

  const splitData = (data, size) => {
    const chunks = [];
    for (let index = 0; index < data.length; index += size) {
      chunks.push(data.slice(index, index + size));
    }
    return chunks;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const payload = { userInputs, MockData };
    const payloadString = JSON.stringify(payload);
    const chunks = splitData(payloadString, 50000);
    let finalResponse = null;

    try {
      setIsSubmitting(true);

      for (const [index, chunk] of chunks.entries()) {
        const response = await fetch("/api/save-listeningmocktestdata", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ chunk, index, totalChunks: chunks.length }),
        });

        if (!response.ok) {
          throw new Error(`Failed to submit listening mock chunk: ${response.status}`);
        }

        finalResponse = await response.json();
      }

      setMessage("Listening mock saved successfully!");
      setNotificationType("success");
      setShowNotification(true);

      setTimeout(() => {
        setIsSubmitting(false);
        navigate(
          finalResponse?.id
            ? `/mockpage/result/${finalResponse.id}`
            : "/mockpage/listeningresult"
        );
      }, 1200);
    } catch (error) {
      console.error("Error submitting listening mock:", error);
      setMessage("Failed to save listening mock.");
      setNotificationType("error");
      setShowNotification(true);
      setIsSubmitting(false);
    }
  };

  const handleTimeout = () => {
    if (hasAutoSubmitted || isSubmitting) return;
    setHasAutoSubmitted(true);
    setShowConfirmDialog(false);
    setShowTimeoutPopup(true);
    handleSubmit();
  };

  const handleNext = () => {
    setShowConfirmDialog(true);
    setNextPage(Math.min(currentPage + 1, totalPages));
  };

  const handleConfirm = () => {
    setShowConfirmDialog(false);
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      setCurrentPage(nextPage);
    }, 100);
  };

  const handlePrevious = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const renderPage = () => {
    if (currentPage === 5) {
      const questionIndex = 0;
      const currentQuestion = MockData.data.SummarizeSpokenText?.[questionIndex];
      return (
        <SummarizeSpokenTestMockPage
          key={currentPage}
          question={currentQuestion}
          onInputChange={(answer) =>
            handleInputChange("SummarizeSpokenText", questionIndex, answer)
          }
          questionNumber={questionIndex + 1}
          maxQuestion={totalQuestionPages}
        />
      );
    }

    if (currentPage >= 6 && currentPage <= 7) {
      const questionIndex = currentPage - 6;
      const currentQuestion = MockData.data.McqSingleAnswer?.[questionIndex];
      return (
        <MCQSingleAnswerMockPage
          key={currentPage}
          question={currentQuestion}
          onInputChange={(answer) =>
            handleInputChange("McqSingleAnswer", questionIndex, answer)
          }
          questionNumber={questionIndex + 2}
          maxQuestion={totalQuestionPages}
        />
      );
    }

    if (currentPage >= 8 && currentPage <= 9) {
      const questionIndex = currentPage - 8;
      const currentQuestion = MockData.data.McqDoubleAnswer?.[questionIndex];
      return (
        <MCQMultipleAnswerMockPage
          key={currentPage}
          question={currentQuestion}
          onInputChange={(answer) =>
            handleInputChange("McqDoubleAnswer", questionIndex, answer)
          }
          questionNumber={questionIndex + 4}
          maxQuestion={totalQuestionPages}
        />
      );
    }

    if (currentPage >= 10 && currentPage <= 11) {
      const questionIndex = currentPage - 10;
      const currentQuestion = MockData.data.FillBlank?.[questionIndex];
      return (
        <FillInTheBlanksMockPage
          key={currentPage}
          question={currentQuestion}
          onInputChange={(answer) =>
            handleInputChange("FillBlank", questionIndex, answer)
          }
          questionNumber={questionIndex + 6}
          maxQuestion={totalQuestionPages}
        />
      );
    }

    if (currentPage >= 12 && currentPage <= 14) {
      const questionIndex = currentPage - 12;
      const currentQuestion = MockData.data.HighlightSummary?.[questionIndex];
      return (
        <HighlightCorrectSummaryMockPage
          key={currentPage}
          question={currentQuestion}
          onInputChange={(answer) =>
            handleInputChange("HighlightSummary", questionIndex, answer)
          }
          questionNumber={questionIndex + 8}
          maxQuestion={totalQuestionPages}
        />
      );
    }

    if (currentPage >= 15 && currentPage <= 16) {
      const questionIndex = currentPage - 15;
      const currentQuestion = MockData.data.MissingWord?.[questionIndex];
      return (
        <SelectMissingWordsMockPage
          key={currentPage}
          question={currentQuestion}
          onInputChange={(answer) =>
            handleInputChange("MissingWord", questionIndex, answer)
          }
          questionNumber={questionIndex + 11}
          maxQuestion={totalQuestionPages}
        />
      );
    }

    if (currentPage >= 17 && currentPage <= 18) {
      const questionIndex = currentPage - 17;
      const currentQuestion = MockData.data.IncorrectWord?.[questionIndex];
      return (
        <HighlightIncorrectWordMockPage
          key={currentPage}
          question={currentQuestion}
          onInputChange={(answer) =>
            handleInputChange("IncorrectWord", questionIndex, answer)
          }
          questionNumber={questionIndex + 13}
          maxQuestion={totalQuestionPages}
        />
      );
    }

    if (currentPage >= 19 && currentPage <= 22) {
      const questionIndex = currentPage - 19;
      const currentQuestion = MockData.data.WritefromDictation?.[questionIndex];
      return (
        <WriteFromDictationMockPage
          key={currentPage}
          question={currentQuestion}
          onInputChange={(answer) =>
            handleInputChange("WritefromDictation", questionIndex, answer)
          }
          questionNumber={questionIndex + 15}
          maxQuestion={totalQuestionPages}
        />
      );
    }

    if (currentPage === 1) return <HeadphoneCheckPage />;
    if (currentPage === 2) return <MicrophoneCheckPage />;
    if (currentPage === 3) return <KeyBoardCheckPage />;
    if (currentPage === 4) return <ListeningSectionTable />;
    return null;
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-gray-800">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="w-full px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-md bg-violet-50 text-violet-700 ring-1 ring-violet-100">
                <Headphones className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-violet-700">
                  PTE Listening Mock Test
                </p>
                <h1 className="text-lg font-extrabold text-gray-900 sm:text-xl">
                  Listening zone only
                </h1>
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[560px]">
              <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <FileAudio2 className="h-4 w-4 text-violet-700" aria-hidden="true" />
                <div>
                  <p className="text-[11px] font-bold uppercase text-gray-500">
                    Progress
                  </p>
                  <p className="text-sm font-extrabold text-gray-900">
                    Page {currentPage}/{totalPages}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <CheckCircle2 className="h-4 w-4 text-violet-700" aria-hidden="true" />
                <div>
                  <p className="text-[11px] font-bold uppercase text-gray-500">
                    Responses
                  </p>
                  <p className="text-sm font-extrabold text-gray-900">
                    {attemptedResponses}/{totalQuestionPages}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-md border border-violet-100 bg-violet-50 px-3 py-2 text-violet-800">
                <Clock3 className="h-4 w-4" aria-hidden="true" />
                <div>
                  <p className="text-[11px] font-bold uppercase">Time</p>
                  <div className="text-sm font-extrabold">
                    {testStarted ? (
                      <Timer
                        initialMinutes={33}
                        initialSeconds={0}
                        Text="Remaining"
                        onComplete={handleTimeout}
                      />
                    ) : (
                      "Not started"
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded-sm bg-slate-100">
            <div
              className="h-full rounded-sm bg-violet-600 transition-all duration-300"
              style={{ width: `${pageProgress}%` }}
            />
          </div>

          {testStarted && (
            <p className="mt-2 text-xs font-semibold text-gray-500">
              Listening question {questionPageNumber} of {totalQuestionPages}
            </p>
          )}
        </div>
      </header>

      <main className="w-full flex-1 px-3 py-5 sm:px-5 lg:px-8">
        <div className="full-mock-ui mx-auto w-full max-w-none">
          {loading ? <Loading /> : renderPage()}
        </div>
      </main>

      {isLoading && <Loading />}
      {showTimeoutPopup && <TimeoutPopup />}
      {showConfirmDialog && (
        <ConfirmDialog
          message="Proceed to the next question?"
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}

      <MockNavigation
        onNext={handleNext}
        onPrevious={handlePrevious}
        disableNext={currentPage === totalPages}
        disablePrevious={currentPage === 1}
        currentPage={currentPage}
        totalPages={totalPages}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />

      {showNotification && (
        <Notification
          message={message}
          type={notificationType}
          onClose={() => setShowNotification(false)}
        />
      )}
    </div>
  );
}
